import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Alert, Button, CircularProgress, Paper, Stack, Typography, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PageLayout from '../components/common/PageLayout';
import { post } from '../utils/api';

/**
 * Full-page confirmation after order save (replaces modal confirmation).
 */
export default function OrderUpdatedPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || '/orders';
  const summary = location.state?.summary;
  const updatedOrder = location.state?.updatedOrder;
  const id = parseInt(String(orderId), 10);
  const [receiptLoading, setReceiptLoading] = React.useState(false);
  const [receiptMessage, setReceiptMessage] = React.useState('');
  const [receiptError, setReceiptError] = React.useState('');
  const customerIdSource = summary?.customerId ?? updatedOrder?.customer_id ?? null;
  const customerId = customerIdSource != null && String(customerIdSource).trim() !== ''
    ? String(customerIdSource)
    : '';

  const handleSendReceipt = async () => {
    setReceiptMessage('');
    setReceiptError('');
    setReceiptLoading(true);
    try {
      await post(`/api/orders/${id}/resend-receipt`, {});
      setReceiptMessage('Receipt email sent.');
    } catch (e) {
      setReceiptError(e?.message || e?.data?.error || 'Failed to send receipt email');
    } finally {
      setReceiptLoading(false);
    }
  };

  if (!Number.isFinite(id) || id <= 0) {
    return (
      <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
        <Typography color="error" sx={{ mb: 2 }}>
          Invalid order ID
        </Typography>
        <Button variant="contained" onClick={() => navigate(returnTo, { state: { orderEditSaved: true } })}>
          Continue
        </Button>
      </PageLayout>
    );
  }

  if (!summary) {
    return (
      <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
        <Typography variant="h5" gutterBottom>
          Order updated
        </Typography>
        <Typography sx={{ mb: 2 }} color="text.secondary">
          No summary was passed (e.g. page refresh). Your changes may already be saved.
        </Typography>
        <Button variant="contained" onClick={() => navigate(returnTo, { state: { orderEditSaved: true } })}>
          Continue
        </Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Typography variant="h5" gutterBottom>
        Order updated
      </Typography>
      <Paper sx={{ p: 3, maxWidth: 640, mx: 'auto', mt: 1 }}>
        <Stack spacing={2} alignItems="center">
          <CheckCircleIcon color="success" sx={{ fontSize: 56 }} aria-hidden />
          <Typography variant="h6" component="h1" textAlign="center">
            Order updated successfully
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Your changes were saved in WooCommerce. You don&apos;t need to save again.
          </Typography>
          <Box
            sx={{
              width: '100%',
              mt: 1,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Typography variant="body2">
              <strong>Order #</strong> {summary.id}
            </Typography>
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              <strong>Status</strong> {summary.status || '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Total</strong> ${Number(summary.total || 0).toFixed(2)}
            </Typography>
            <Typography variant="body2">
              <strong>Customer</strong> {summary.customerLabel}
            </Typography>
          </Box>
          {(receiptMessage || receiptError) && (
            <Box sx={{ width: '100%' }}>
              {receiptMessage ? <Alert severity="success" onClose={() => setReceiptMessage('')}>{receiptMessage}</Alert> : null}
              {receiptError ? (
                <Alert severity="error" onClose={() => setReceiptError('')}>
                  {receiptError}
                </Alert>
              ) : null}
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2, width: '100%' }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(`/orders/${id}/edit`, { state: { returnTo } })}
            >
              Edit again
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={receiptLoading ? <CircularProgress size={18} color="inherit" aria-hidden /> : <EmailOutlinedIcon />}
              disabled={receiptLoading}
              onClick={handleSendReceipt}
            >
              Send email receipt
            </Button>
            <Button
              variant="outlined"
              fullWidth
              disabled={!customerId}
              onClick={() => customerId && navigate(`/devotees/${encodeURIComponent(customerId)}`)}
            >
              View Profile/Orders
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(returnTo, { state: { orderEditSaved: true } })}
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </PageLayout>
  );
}
